import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction } from '../../store';
import { urlParamReplicaId, urlProjectDocumentsAddNote } from '../../../Constants';
import { ViewIconDocumentReleaseStatus } from './sliceProjectDocuments';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentAddNoteState = {
    isDialogOpen: false,
    loading: false,
    error: null,
    isNoteAdded: false,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    openAddNoteDialog(state: IProjectDocumentAddNoteState, action: IAction<boolean>) {
        state.isDialogOpen = action.payload;
        if (action.payload === false) {
            state.isNoteAdded = false;
        }
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function to add notes to documents.
     * @param config - Object that contains the project database's universalId, replicaId and formData to return.
     */
    getProjectDocumentsAddNote: async (config: IProjectDocumentAddNoteConfig): Promise<void> => {
        try {
            const {
                universalIds,
                replicaId,
                formData: { subject, noteComment, visibility },
            } = config;

            const formData = new FormData();
            formData.append(
                'document',
                JSON.stringify({
                    subject,
                    noteComment,
                    ViewIcon_DocumentRelease: visibility,
                }),
            );
            formData.append('universalIds', universalIds.join('|'));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectDocumentsAddNote.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentAddNoteState>) => {
        /**
         * Reducers for the 'getProjectDocumentsAddNote' thunk.
         */
        builder.addCase(
            getProjectDocumentsAddNote.pending,
            (state: Draft<IProjectDocumentAddNoteState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectDocumentsAddNote.fulfilled,
            (state: Draft<IProjectDocumentAddNoteState>) => {
                state.loading = false;
                state.error = null;
                state.isNoteAdded = true;
            },
        );
        builder.addCase(
            getProjectDocumentsAddNote.rejected,
            (state: Draft<IProjectDocumentAddNoteState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocumentsAddNote = createAsyncThunk(
    'projectWorkflowHistory',
    thunks.getProjectDocumentsAddNote,
);

/**
 * Interface that describes the project document's add note state.
 */
export interface IProjectDocumentAddNoteState {
    /** Defines if Add Note Dialog is open. */
    isDialogOpen: boolean;
    /** Defines if Form is saving. */
    loading: boolean;
    /** Error that is thrown during resource fetching. */
    error: SerializedError;
    isNoteAdded: boolean;
}

/**
 * Interface that describes the document's details config.
 */
export interface IProjectDocumentAddNoteConfig {
    /** Project replicaId. */
    replicaId: string;

    /** Document's universalId. */
    universalIds: string[];

    /** Schema. */
    formData: IProjectDocumentAddNoteFields;
}

/**
 * Interface that describes the project document add note fields.
 */

export interface IProjectDocumentAddNoteFields {
    subject: string;
    noteComment: string;
    visibility: ViewIconDocumentReleaseStatus;
}

/**
 * Create the redux slice.
 */
export const projectDocumentAddNote = createSlice({
    name: 'projectDocumentsAddNote',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { openAddNoteDialog } = projectDocumentAddNote.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentAddNote.reducer;
